import React, { useCallback, useRef } from 'react';
import styled from 'styled-components/macro';
import { TeamIcon } from 'assets/icons';
import {
  ChannelFilterSelectionFragment,
  FilterSelectionType,
  useDeleteChannelFilterSelectionMutation,
} from 'shared/types/graphql';
import {
  Text,
  Flex,
  Button,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@televet/kibble-ui';

interface IArchiveChannelAlertDialogProps {
  channelFilterSelection: ChannelFilterSelectionFragment | undefined;
  isOpen: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const DeleteChannelListFilterAlertDialog = ({
  channelFilterSelection,
  isOpen,
  onSuccess,
  onCancel,
}: IArchiveChannelAlertDialogProps): JSX.Element => {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [deleteChannelFilterSelection, { loading: isDeletingChannelFilterSelection }] =
    useDeleteChannelFilterSelectionMutation({
      refetchQueries: ['getChannelFilterSelections'],
      awaitRefetchQueries: true,
    });

  const handleDeleteFilterClick = useCallback(async () => {
    if (!channelFilterSelection?.id) return;
    await deleteChannelFilterSelection({
      variables: { channelFilterSelectionId: channelFilterSelection.id },
    });
    onSuccess();
  }, [channelFilterSelection, deleteChannelFilterSelection, onSuccess]);

  return (
    <AlertDialog size="sm" isOpen={isOpen} isCentered={true} leastDestructiveRef={cancelRef} onClose={onCancel}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="30px" fontWeight="bold">
            Delete Saved Filter
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text fontWeight="bold">
              Are you sure you want to delete{' '}
              {channelFilterSelection?.name ? `'${channelFilterSelection?.name}'` : 'this filter'}?
            </Text>
            {channelFilterSelection?.filterSelectionType === FilterSelectionType.PublicReadonly && (
              <Flex
                align="baseline"
                border="1px"
                borderColor="border.danger"
                bg="background.danger"
                borderRadius="8px"
                py="3"
                px="4"
                mt="4"
              >
                <StyledTeamIcon />
                <Flex direction="column" ml="3">
                  <Text fontWeight="bold" mb="1">
                    Shared with team
                  </Text>
                  <Text>Your team will lose access to this saved filter if you delete it.</Text>
                </Flex>
              </Flex>
            )}
          </AlertDialogBody>
          <AlertDialogFooter justifyContent="space-between">
            <Button ref={cancelRef} size="md" variant="tertiary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="md"
              variant="primaryDestructive"
              minWidth="72px"
              isLoading={isDeletingChannelFilterSelection}
              onClick={handleDeleteFilterClick}
            >
              Delete Filter
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const StyledTeamIcon = styled(TeamIcon)`
  flex: 0 0 auto;
  width: 18px;
  height: auto;

  * {
    fill: #3f445a;
  }
`;

export default DeleteChannelListFilterAlertDialog;
